import { type ReactNode, useState } from 'react';

import { Collapse } from '@lib/ui';
import { cva } from '@lib/utils';

const sidebarNavGroupHeaderVariants = cva([
  // Sticky positioning
  'sticky top-0 z-10',
  // Layout
  'relative px-6',
  // Background (matches sidebar - needed so items don't show through when scrolling)
  'bg-surface',
]);

const sidebarNavGroupButtonVariants = cva([
  // Layout
  'mb-2 flex cursor-pointer items-center gap-3 pt-2',
  // Typography
  'text-xs font-medium uppercase tracking-wider',
  // Colors
  'text-fg-muted',
  // Hover/Focus
  'hover:text-fg focus:text-fg',
  // Transitions
  'outline-hidden transition-colors',
]);

const scrollFadeVariants = cva([
  // Positioning
  'pointer-events-none absolute inset-x-0 -bottom-3 h-3',
  // Gradient fade effect (matches sidebar background)
  'bg-linear-to-b from-surface to-transparent',
]);

export interface SidebarNavGroupProps {
  /** Group label displayed as section header */
  label: string;
  /** Child nav items (SidebarNavItem or SidebarNavCollapsible) */
  children: ReactNode;
  /** Whether the group is expanded by default */
  defaultOpen?: boolean;
}

export function SidebarNavGroup({ label, children, defaultOpen = true }: SidebarNavGroupProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggle = () => setIsOpen((prev) => !prev);

  return (
    <div className="pt-3">
      <div className={sidebarNavGroupHeaderVariants()}>
        <button type="button" onClick={toggle} className={sidebarNavGroupButtonVariants()}>
          <span>{label}</span>
        </button>
        {/* Scroll fade effect */}
        <div className={scrollFadeVariants()} />
      </div>
      <Collapse in={isOpen}>
        <div className="flex flex-col space-y-1.5">{children}</div>
      </Collapse>
    </div>
  );
}

SidebarNavGroup.displayName = 'SidebarNavGroup';
